import {
  FintracFlattenedFormFieldConfig,
  FintracFlattenedFormFieldConfigs,
  FintracForm,
  FintracFormField,
  FintracFormFields,
  FintracFormFieldSection,
  FintracFormSection,
} from 'app/modules/fintracStr/models';
import { isArray, isEqual } from 'lodash';

export const addFlattenedFormFieldConfig = (
  acc: Record<string, FintracFlattenedFormFieldConfigs>,
  path: string,
  config: FintracFormField | FintracFormSection | FintracFormFieldSection,
): void => {
  const baseConfig: FintracFlattenedFormFieldConfig = {
    label: config.label,
    repeatable: config.repeatable,
    type: config.type,
    hideIfTrue: config.hideIfTrue,
    mustExistInPayload: config.mustExistInPayload,
    mustHaveValueIfExist: config.mustHaveValueIfExist,
    payloadToFormValueConversion:
      'payloadToFormValueConversion' in config
        ? config.payloadToFormValueConversion
        : undefined, // Tab sections does not convert form values
    formToPayloadValueConversion:
      'formToPayloadValueConversion' in config
        ? config.formToPayloadValueConversion
        : undefined, // Tab sections does not convert form values
    payloadErrorPathToFormErrorPathConversion:
      'payloadErrorPathToFormErrorPathConversion' in config
        ? config.payloadErrorPathToFormErrorPathConversion
        : undefined,
  };

  // Ensure internal field paths remain single-config and do not get overwritten inadvertently.
  acc[path] = acc[path] ?? [baseConfig];

  // This is needed so that the error paths given in the payload have a field to map to.
  // Without this, the error will not be shown since it dosent know which field to target.
  // Note that this relationship is a one to many relationship, which means one error paths can map to multiple form fields
  if (baseConfig.payloadErrorPathToFormErrorPathConversion) {
    const { payloadPath } =
      baseConfig.payloadErrorPathToFormErrorPathConversion([], true);

    for (const ppath of payloadPath) {
      const existing = acc[ppath] ?? [];
      // Avoid self-referential duplicates that would create multi-configs for internal fields and avoid duplicate references
      // This comparison works since the baseConfig reference is the same for all instances within the acc array
      if (
        ppath !== path &&
        !existing.some((existingConfig) => isEqual(existingConfig, baseConfig))
      ) {
        acc[ppath] = [...existing, baseConfig];
      }
    }
  }
};

/**
 * Flattens the FINTRAC_STR_FORM_FIELDS configuration into a single-level object.
 * This function converts the nested form structure into a flat object where:
 * - Keys are full paths to fields (e.g., 'transactions[0].amount' or 'reportDetails.reportingEntityNumber')
 * - Values are the corresponding field configurations
 * - Repeatable sections use [*] notation in paths
 * - Nested arrays are flattened into a single level
 *
 * Example:
 * Input: [{ tabKey: 'reportDetails', tabSection: { fields: [{ name: 'reportingEntityNumber' }] } }]
 * Output: { 'reportDetails.reportingEntityNumber': { name: 'name', ... } }
 *
 * @param formFields - Array of form configurations containing tab sections and their fields
 * @returns A flat object mapping field paths to their configurations
 */
export const flattenFormConfig = (
  formFields: FintracForm[],
): Record<string, FintracFlattenedFormFieldConfigs> => {
  const result: Record<string, FintracFlattenedFormFieldConfigs> = {};

  for (const { tabSection } of formFields) {
    const { name, repeatable, fields } = tabSection;
    const path = repeatable ? `${name}[*]` : name;

    flattenFields(fields, result, path);
    addFlattenedFormFieldConfig(result, path, tabSection);
  }

  return result;
};

const flattenFields = (
  fields: FintracFormFields,
  accumulator: Record<string, FintracFlattenedFormFieldConfigs>,
  path: string = '',
): void => {
  for (const field of fields) {
    if (isArray(field)) {
      for (const f of field) {
        const fieldPath = `${path}.${f.name}`;
        const repeatablePath = f.repeatable ? `${fieldPath}[*]` : fieldPath;
        addFlattenedFormFieldConfig(accumulator, repeatablePath, f);
      }
    } else if (field.type === 'section') {
      const newPath = path ? `${path}.${field.name}` : field.name;
      const repeatablePath = field.repeatable ? `${newPath}[*]` : newPath;
      addFlattenedFormFieldConfig(accumulator, repeatablePath, field);
      flattenFields(field.fields, accumulator, repeatablePath);
    } else {
      const newPath = path ? `${path}.${field.name}` : field.name;
      const repeatablePath = field.repeatable ? `${newPath}[*]` : newPath;
      addFlattenedFormFieldConfig(accumulator, repeatablePath, field);
    }
  }
};
